import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ArcButton } from '../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../common/components/SlidingAnimationPageBase';
import { useParamBackUrl } from '../../common/hooks/use-param-back-url';
import { CircleIcon, GridIcon } from '../../styles/custom-icons';
import { selectSite } from '../site/siteSlice';
import { UpdateZipcodeButton } from '../site/UpdateZipcodeButton';
import { RetailerSelect } from './RetailerSelect';
import { useTranslatedDefaultTariffData } from './tariff-constants';
import { useRetailRates } from './tariffApi';
import { useTariffFormContext } from './useTariffFormContext';
import { WattbuyRetailRatesSelect } from './WattbuyRetailRatesSelect';

export const TariffSetup = () => {
  const { errors, setValue, control, setError, reset } = useTariffFormContext();
  const { post_code: postCode } = useSelector(selectSite);
  const navigate = useNavigate();
  const utility = useWatch({ name: 'tariff.utility', control });
  const linkButtonColor = useColorModeValue('schneiderSkyBlue.600', 'schneiderSkyBlue.200');
  const { t } = useTranslation();
  const DEFAULT_TARIFF_DATA = useTranslatedDefaultTariffData();
  const backURL = useParamBackUrl(`../../home?direction=back`);
  const toast = useToast({ duration: 3000, isClosable: true });

  // Prefetch retail rates so we know if the user can proceed with auto tariff setup via Wattbuy.
  const { retailRates, isLoading, error: retailRatesError } = useRetailRates();
  const userCanSetupWattbuyTariff = useMemo(() => {
    return !!utility.id && retailRates.length > 0;
  }, [utility, retailRates]);

  const handleSubmit = (params = '') => {
    const isUtilityIdInvalid = utility.id == null;
    if (isUtilityIdInvalid) {
      setError('tariff.utility.id', { message: t('Energy Rates.select retailer') });
    } else {
      // If the user has selected a utility that doesnt have any retail rates, they are taken to manual setup
      handleNavigateToManualSetup(params);
    }
  };

  const handleNavigateToManualSetup = (params = '') => {
    // Before we initiate manual setup, reset any form tariff data (can linger after navigating back from auto setup)
    reset({
      ...DEFAULT_TARIFF_DATA,
      tariff: { ...DEFAULT_TARIFF_DATA.tariff, utility },
    });
    setValue('tariff.planName', t('Energy Rates.manual tariff'), { shouldDirty: true });
    navigate(`../tariff-details${params}`);
  };

  useEffect(() => {
    if (!isLoading && retailRatesError && !toast.isActive('retail-rates-fetch-error')) {
      toast({
        title: t('Energy Rates.error fetching retail rates'),
        description: `${t('Common.please try again')} ${t('Energy Rates.if error persists fallback to manual')}`,
        status: 'error',
        id: 'fetch-retail-rates-error',
      });
    }
  }, [isLoading, retailRatesError, t, toast]);

  return (
    <SlidingAnimationPageBase
      title={t('Energy Rates.set up rates')}
      includeTopNav
      px={4}
      backURL={backURL}
      data-testid="tariff-setup"
    >
      <Center position="relative" mb={1} flexDirection="column" textAlign="center">
        <Box w="80px" position={'absolute'}>
          <GridIcon w="100%" h="100%" color="#9FA0A4" data-testid={`grid-icon`} />
        </Box>
        <CircleIcon h="60%" w="60%" color="#9FA0A4" data-testid="circle-icon" zIndex={3} />
      </Center>
      <Heading textAlign="center" fontSize={24}>
        {t('Energy Rates.select retailer')}
      </Heading>
      <Text mb={5} textAlign="center">
        {t('Energy Rates.use the dropdown to select')}
      </Text>

      <FormControl isInvalid={!!errors?.tariff?.utility?.id?.message} data-testid="retailer-select">
        <Controller
          control={control}
          name="tariff.utility.name"
          render={() => (
            <RetailerSelect
              selectedRetailerId={utility.id}
              selectedRetailerName={utility.name}
              postCode={postCode}
              onSelect={(id, name, state) => {
                // When retailer changes, reset any form tariff data (can linger after navigating back from auto setup)
                reset({ ...DEFAULT_TARIFF_DATA });
                setValue('tariff.utility', { id, name, state }, { shouldDirty: true });
              }}
            />
          )}
        />
        <FormErrorMessage>{errors?.tariff?.utility?.id?.message}</FormErrorMessage>
      </FormControl>

      <Flex fontSize={14} my={2}>
        {t('Energy Rates.options based on')} <Text mx={1}>{postCode} - </Text>
        <UpdateZipcodeButton
          onZipcodeUpdated={(_zipCode) => {
            reset({ ...DEFAULT_TARIFF_DATA });
          }}
        />
      </Flex>

      <Center mt={10} flexDir={'column'}>
        {userCanSetupWattbuyTariff ? (
          <WattbuyRetailRatesSelect />
        ) : (
          <ArcButton
            arcColor="#3DCD58"
            minWidth="280px"
            py={6}
            mb={4}
            mt="auto"
            onClick={() => handleSubmit('?showRatesNotFoundCopy=true')}
            data-testid="continue-manual-setup-button"
            isLoading={isLoading}
          >
            {t('Common.continue')}
          </ArcButton>
        )}
        {/* Users can bypass auto setup process via button below */}
        <Button
          variant="link"
          minWidth={'fit-content'}
          w="fit-content"
          fontWeight={400}
          fontSize={15}
          mb={6}
          color={linkButtonColor}
          onClick={() => handleNavigateToManualSetup()}
          data-testid="manual-setup-button"
          isLoading={isLoading}
        >
          {t('Energy Rates.set up manually').toUpperCase()}
        </Button>
      </Center>
    </SlidingAnimationPageBase>
  );
};
